import {
  useId,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from "react";
import { css, cx } from "@strust/ui/css";
import { control, field } from "@strust/ui/recipes";
import { EyeIcon, EyeOffIcon } from "../icons/icons.tsx";

export interface FieldChromeProps {
  /** Visible label (from i18n in the app); always present for assistive technology. */
  readonly label: string;
  /** Visually hides the label (it stays the accessible name). */
  readonly hideLabel?: boolean;
  /** Help shown under the field and announced with it. */
  readonly hint?: ReactNode;
  /** Validation message; marks the field invalid and is announced with it. */
  readonly error?: string | undefined;
  /** Shows the required mark next to the label (the input also gets `required`). */
  readonly required?: boolean | undefined;
  readonly className?: string;
}

export function describedBy(
  id: string,
  hint: unknown,
  error: unknown,
  extra?: string,
): string | undefined {
  const ids = [hint ? `${id}-hint` : undefined, error ? `${id}-error` : undefined, extra].filter(
    Boolean,
  );
  return ids.length > 0 ? ids.join(" ") : undefined;
}

/** Label row, control, help, error and counter of a field (shared by the field primitives). */
export function FieldFrame({
  id,
  labelId,
  label,
  hideLabel,
  hint,
  error,
  required,
  className,
  counter,
  children,
}: FieldChromeProps & {
  readonly id: string;
  /** Id of the `<label>`, for a popup that is named by it (`aria-labelledby`). */
  readonly labelId?: string;
  readonly counter?: ReactNode;
  readonly children: ReactNode;
}) {
  const s = field();
  return (
    <div className={cx(s.root, className)}>
      <div className={hideLabel ? css({ srOnly: true }) : s.labelRow}>
        <label htmlFor={id} id={labelId} className={s.label}>
          {label}
        </label>
        {required ? (
          <span aria-hidden="true" className={s.required}>
            *
          </span>
        ) : null}
      </div>
      {children}
      {hint || counter ? (
        <div className={s.foot}>
          {hint ? (
            <span id={`${id}-hint`} className={s.help}>
              {hint}
            </span>
          ) : (
            <span />
          )}
          {counter}
        </div>
      ) : null}
      {error ? (
        <span id={`${id}-error`} className={s.error}>
          {error}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The control with an optional decorative icon inside its start edge (the design's inputs with
 * leading icons). The icon never takes pointer events and is hidden from assistive technology.
 */
export function IconSlot({
  icon,
  children,
}: {
  readonly icon: ReactNode | undefined;
  readonly children: ReactNode;
}) {
  if (!icon) return <>{children}</>;
  return (
    <div className={css({ position: "relative", display: "flex", alignItems: "center" })}>
      <span
        aria-hidden="true"
        className={css({
          position: "absolute",
          insetInlineStart: "3.5",
          display: "inline-flex",
          color: "text.2",
          pointerEvents: "none",
          "& svg": { width: "icon.lg", height: "icon.lg" },
        })}
      >
        {icon}
      </span>
      {children}
    </div>
  );
}

export interface TextFieldProps
  extends
    FieldChromeProps,
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      "id" | "aria-invalid" | "aria-describedby" | "className" | "size" | "required"
    > {
  /** Monospace value (identifiers, IPs, codes). */
  readonly mono?: boolean;
  readonly size?: "sm" | "md";
  /** Decorative icon inside the field, before the value (mail, lock). */
  readonly leadingIcon?: ReactNode;
  /**
   * Password reveal toggle (only for `type="password"`): the accessible names of the button while
   * the value is hidden ("Show password") and shown ("Hide password"), from i18n in the app.
   */
  readonly revealLabels?: { readonly show: string; readonly hide: string };
}

const ADORNED = css({ position: "relative", display: "flex", alignItems: "center" });
const LEADING = css({
  position: "absolute",
  insetInlineStart: "3",
  display: "inline-flex",
  color: "text.3",
  pointerEvents: "none",
  "& svg": { width: "icon.md", height: "icon.md" },
});
const REVEAL = css({
  position: "absolute",
  insetInlineEnd: "1.5",
  display: "inline-grid",
  placeItems: "center",
  width: "control.sm",
  height: "control.sm",
  color: "text.2",
  bg: "transparent",
  borderWidth: "0",
  borderRadius: "sm",
  cursor: "pointer",
  _hover: { color: "text" },
  _focusVisible: { outline: "none", boxShadow: "focus" },
  "& svg": { width: "icon.md", height: "icon.md" },
});
const WITH_LEADING = css({ ps: "10" });
const WITH_REVEAL = css({ pe: "10" });

/** Labelled text input with hint and error wired through `aria-describedby`. */
export function TextField({
  label,
  hideLabel,
  hint,
  error,
  required,
  className,
  mono = false,
  size = "md",
  leadingIcon,
  revealLabels,
  ...input
}: TextFieldProps) {
  const id = useId();
  const [revealed, setRevealed] = useState(false);
  const reveal = revealLabels !== undefined && input.type === "password";
  const inputClass = control({ kind: mono ? "mono" : "input", size });
  const adorned = leadingIcon !== undefined || reveal;
  return (
    <FieldFrame
      id={id}
      label={label}
      {...(hideLabel === undefined ? {} : { hideLabel })}
      hint={hint}
      error={error}
      {...(required === undefined ? {} : { required })}
      {...(className === undefined ? {} : { className })}
    >
      {(() => {
        const element = (
          <input
            {...input}
            {...(reveal && revealed ? { type: "text" } : {})}
            id={id}
            required={required}
            aria-invalid={error ? true : undefined}
            aria-describedby={describedBy(id, hint, error)}
            className={cx(
              inputClass,
              leadingIcon !== undefined && WITH_LEADING,
              reveal && WITH_REVEAL,
            )}
          />
        );
        if (!adorned) return element;
        return (
          <div className={ADORNED}>
            {leadingIcon !== undefined ? (
              <span aria-hidden="true" className={LEADING}>
                {leadingIcon}
              </span>
            ) : null}
            {element}
            {reveal ? (
              <button
                type="button"
                className={REVEAL}
                aria-label={revealed ? revealLabels.hide : revealLabels.show}
                aria-pressed={revealed}
                onClick={() => {
                  setRevealed((value) => !value);
                }}
              >
                {revealed ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            ) : null}
          </div>
        );
      })()}
    </FieldFrame>
  );
}

export interface TextareaFieldProps
  extends
    FieldChromeProps,
    Omit<
      TextareaHTMLAttributes<HTMLTextAreaElement>,
      "id" | "aria-invalid" | "aria-describedby" | "className" | "required"
    > {
  /** Shows "<length> / <maxLength>" under the field when `maxLength` is set. */
  readonly showCounter?: boolean;
  /** Accessible text of the counter, e.g. "104 of 500 characters" (from i18n). */
  readonly counterLabel?: string | undefined;
}

/** Labelled textarea with help text and an optional character counter. */
export function TextareaField({
  label,
  hideLabel,
  hint,
  error,
  required,
  className,
  showCounter = false,
  counterLabel,
  ...textarea
}: TextareaFieldProps) {
  const id = useId();
  const length = typeof textarea.value === "string" ? textarea.value.length : 0;
  const counter =
    showCounter && textarea.maxLength !== undefined ? (
      <span className={field().counter}>
        <span aria-hidden="true">{`${String(length)} / ${String(textarea.maxLength)}`}</span>
        {counterLabel ? <span className={css({ srOnly: true })}>{counterLabel}</span> : null}
      </span>
    ) : undefined;
  return (
    <FieldFrame
      id={id}
      label={label}
      {...(hideLabel === undefined ? {} : { hideLabel })}
      hint={hint}
      error={error}
      {...(required === undefined ? {} : { required })}
      {...(className === undefined ? {} : { className })}
      counter={counter}
    >
      <textarea
        {...textarea}
        id={id}
        required={required}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy(id, hint, error)}
        className={control({ kind: "textarea" })}
      />
    </FieldFrame>
  );
}

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

export interface SelectProps
  extends
    FieldChromeProps,
    Omit<
      SelectHTMLAttributes<HTMLSelectElement>,
      "id" | "value" | "onChange" | "className" | "size" | "required"
    > {
  readonly value: string;
  readonly options: readonly SelectOption[];
  readonly onChange: (value: string) => void;
  readonly mono?: boolean;
  readonly size?: "sm" | "md";
  /** Decorative icon inside the start edge of the select. */
  readonly leadingIcon?: ReactNode;
}

/** Native select with a label: keyboard and screen-reader behavior come from the platform. */
export function Select({
  label,
  hideLabel,
  hint,
  error,
  required,
  className,
  value,
  options,
  onChange,
  mono = false,
  size = "md",
  leadingIcon,
  ...select
}: SelectProps) {
  const id = useId();
  return (
    <FieldFrame
      id={id}
      label={label}
      {...(hideLabel === undefined ? {} : { hideLabel })}
      hint={hint}
      error={error}
      {...(required === undefined ? {} : { required })}
      {...(className === undefined ? {} : { className })}
    >
      <IconSlot icon={leadingIcon}>
        <select
          {...select}
          id={id}
          required={required}
          value={value}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy(id, hint, error)}
          className={cx(
            control({ kind: "select", size, icon: leadingIcon !== undefined }),
            mono ? css({ fontFamily: "mono", fontSize: "md" }) : undefined,
          )}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
            onChange(event.target.value);
          }}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
      </IconSlot>
    </FieldFrame>
  );
}

export interface DateRangeFieldProps {
  /** Group label (e.g. "Period"). */
  readonly label: string;
  readonly fromLabel: string;
  readonly toLabel: string;
  readonly from: string;
  readonly to: string;
  readonly onChange: (range: { readonly from: string; readonly to: string }) => void;
  /** `date` or `datetime-local` inputs. */
  readonly type?: "date" | "datetime-local";
  readonly hint?: ReactNode;
  readonly error?: string | undefined;
  readonly className?: string;
}

/** Two native date inputs (from, to) grouped under one label. */
export function DateRangeField({
  label,
  fromLabel,
  toLabel,
  from,
  to,
  onChange,
  type = "date",
  hint,
  error,
  className,
}: DateRangeFieldProps) {
  const id = useId();
  const s = field();
  const row = css({ display: "flex", alignItems: "center", gap: "2", flexWrap: "wrap" });
  return (
    <fieldset
      className={cx(s.root, css({ border: "none", m: "0", p: "0", minWidth: "0" }), className)}
      aria-describedby={describedBy(id, hint, error)}
    >
      <legend className={cx(s.label, css({ p: "0", mb: "1.5" }))}>{label}</legend>
      <div className={row}>
        <input
          type={type}
          aria-label={fromLabel}
          value={from}
          max={to || undefined}
          aria-invalid={error ? true : undefined}
          className={cx(control(), css({ width: "auto", flex: "1", minWidth: "40" }))}
          onChange={(event) => {
            onChange({ from: event.target.value, to });
          }}
        />
        <span aria-hidden="true" className={css({ color: "text.2" })}>
          →
        </span>
        <input
          type={type}
          aria-label={toLabel}
          value={to}
          min={from || undefined}
          aria-invalid={error ? true : undefined}
          className={cx(control(), css({ width: "auto", flex: "1", minWidth: "40" }))}
          onChange={(event) => {
            onChange({ from, to: event.target.value });
          }}
        />
      </div>
      {hint ? (
        <span id={`${id}-hint`} className={s.help}>
          {hint}
        </span>
      ) : null}
      {error ? (
        <span id={`${id}-error`} className={s.error}>
          {error}
        </span>
      ) : null}
    </fieldset>
  );
}
